import type { CameraView, CountryQuizConfig, RegionScope } from '../config';
import { CONTINENT_CAMERAS } from '../framework/filterSlug';

/**
 * Inputs for `makeCountryQuiz`. Per-region values that vary; everything
 * else (audio, layer setup, attempts-per-country, end condition, etc.)
 * is shared across every region quiz so the factory bakes in the defaults.
 *
 * Adding a new continent quiz: one entry in `country-quizzes.ts` calling
 * `makeCountryQuiz({...})` — no factory change needed.
 *
 * The same pattern will host admin1 quizzes (US states, Indian states, ...)
 * via a sibling factory once that schema lands; they'll share the same
 * audio + layer defaults so only camera + filter need per-mode values.
 */
export interface CountryQuizSpec {
  /** Region slug — becomes part of the URL (`/play/countries-{region}/quiz`). */
  region: RegionScope;
  /** Display name shown in the sidebar + page title. */
  displayName: string;
  /** Optional shorter sidebar label; falls back to displayName. */
  shortName?: string;
  /**
   * `Country.continent` string to match. Values from the dataset: "Europe",
   * "Africa", "Asia", "North America", "South America", "Oceania" (also
   * "Antarctica" and "Seven seas (open ocean)" but those won't host quizzes).
   */
  continent: string;
  /**
   * Optional override for the initial camera framing. Defaults to
   * `CONTINENT_CAMERAS[region]` so per-continent framing tweaks happen
   * in one place; only override here when a specific quiz wants
   * different framing than the practice-mode filter for the same
   * continent.
   */
  cameraView?: CameraView;
  /**
   * Force-include these ISOs even if they're not in the UN-members
   * baseline. Used for region quirks — e.g. Africa includes Western
   * Sahara (`EH`) which isn't a UN member but is unambiguously
   * African and recognizable enough to include in the quiz.
   */
  additionalIsos?: string[];
}

/**
 * Default audio for all country quizzes. Same chord/tone the country
 * practice mode uses, so the auditory feel transfers across formats.
 */
const QUIZ_AUDIO = {
  // Bright C major chord — same "great guess" reward as practice.
  correctFrequencies: [523.25, 659.25, 783.99],
  // Low A3 — neutral, not a buzzer.
  incorrectFrequencies: 220,
};

/** Standard 3-strikes-then-reveal that the user spec'd. */
const ATTEMPTS_PER_COUNTRY = 3;

export function makeCountryQuiz(spec: CountryQuizSpec): CountryQuizConfig {
  return {
    id: `countries-${spec.region}-quiz`,
    scope: { entity: 'countries', region: spec.region },
    format: 'quiz',
    interaction: 'click-in-region',

    displayName: spec.displayName,
    shortName: spec.shortName,

    // Same flat-gray globe as the country practice mode — no photo
    // texture (Play.svelte derives skipTexture from texture.visible).
    layers: {
      texture: { visible: false, opacity: 0 },
      borders: { visible: true, opacity: 1.0 },
      fills: { visible: true, opacity: 1.0 },
      hover: true,
    },
    userMayToggle: {
      texture: false,
      borders: true,
      fills: false,
      hover: true,
    },

    unitSystem: 'miles',
    countryFilter: {
      continents: [spec.continent],
      // UN members only — matches the standard "193 countries" set most
      // players will recognize from Seterra etc. Curated set lives in
      // `framework/curatedSets.ts`.
      setName: 'un-members',
      // Per-region exceptions (e.g. Western Sahara on Africa) layer
      // on top of the UN-members baseline.
      additionalIsos: spec.additionalIsos,
    },
    attemptsPerCountry: ATTEMPTS_PER_COUNTRY,
    endCondition: { type: 'all-correct' },
    cameraView: spec.cameraView ?? CONTINENT_CAMERAS[spec.region],
    audio: QUIZ_AUDIO,
  } as CountryQuizConfig;
}
